// The app itself. src/index.tsx loads the viewer's language first, then this
// module, so text defined at the top of any module is already translated.
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AdminAuthProvider } from "./context/AdminAuthContext";
import App from './App';
import AppErrorBoundary from './components/AppErrorBoundary';
import { AuthProvider } from "./context/AuthContext";
import { DesktopShell } from "./desktop/DesktopShell";
import { registerServiceWorker } from "./lib/pushClient";
import { hideBootSpinner } from "./lib/bootSpinner";

const isAdmin = window.location.pathname.startsWith("/admin");

// Desktop mode = running inside the Electron companion. We detect two ways:
//   (1) preload.js sets window.electronAPI.isElectron — most reliable
//   (2) ?mode=desktop query param — survives early redirects, useful when
//        loading the page in a normal browser for testing the desktop layout
// Either is enough to trigger the floating-button/panel shell.
const isDesktopMode =
  !!(window as any).electronAPI?.isElectron ||
  new URLSearchParams(window.location.search).get("mode") === "desktop";

if (isDesktopMode) {
  document.body.classList.add("desktop-electron-mode");
}

// Service worker drives web push, which Electron handles natively (or not
// at all for MVP). Skip registration in desktop mode to avoid spurious
// "Notification permission denied" prompts inside the overlay.
if (!isAdmin && !isDesktopMode) {
  registerServiceWorker();
}

// First-launch-in-PWA: pop the window at a tidy menubar-style size so DJs see
// a compact overlay instead of a fullscreen browser window. Only fires once
// (localStorage flag) and only in standalone mode.
//
// Skipped in desktop mode — the Electron main process owns window sizing
// and any window.resizeTo() call here would fight with set-expanded IPC.
if (!isDesktopMode) {
  try {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone && !localStorage.getItem("requestapp.firstResize")) {
      setTimeout(() => {
        try {
          window.resizeTo(420, 720);
          localStorage.setItem("requestapp.firstResize", "1");
        } catch {
          /* Safari may block this — ignore */
        }
      }, 300);
    }
  } catch {
    /* storage disabled, ignore */
  }
}


const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// AppErrorBoundary wraps EVERYTHING (build 41). A render error that escapes
// unmounts React and leaves #root empty — and an empty #root is a pure-black,
// force-quit-only screen inside the iOS app (nothing paints `body`, and the
// webview falls back to UIColor.systemBackground). The boundary guarantees a
// painted screen with a Reload button instead, on every path.
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      {isAdmin ? (
        <AdminAuthProvider>
          <App />
        </AdminAuthProvider>
      ) : (
        <AuthProvider>
          {isDesktopMode ? (
            <DesktopShell>
              <App />
            </DesktopShell>
          ) : (
            <App />
          )}
        </AuthProvider>
      )}
    </AppErrorBoundary>
  </React.StrictMode>
);

// The boot spinner (public/index.html) stays up until App has checked the
// sign-in and has a real screen to show — App hides it (lib/bootSpinner). A
// crash hides it too (AppErrorBoundary). The safety net: whatever happens, it
// leaves after 15 s, uncovering the in-app Loader or the screen behind it.
setTimeout(hideBootSpinner, 15000);
